import { type CSSProperties, type ReactNode, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { TopLoadingBar } from "@/components/skeletons";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
    Bot,
    Building2,
    ChevronDown,
    Coins,
    CreditCard,
    FileCheck,
    FileText,
    HardDrive,
    Home,
    KeyRound,
    LogOut,
    Mail,
    Menu,
    MessageCircle,
    Mic,
    PlusSquare,
    Settings,
    Share2,
    SlidersHorizontal,
    Ticket,
    Upload,
    UserCog,
    Users,
    UserCircle,
    Video,
    Workflow,
    type LucideIcon,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useCompanyStatus } from "@/hooks/useCompanyStatus";
import { PaymentDueBanner } from "@/components/PaymentDueBanner";
import { useNavItems, type NavItem } from "@/hooks/useNavItems";
import { SuspendedOverlay } from "@/components/SuspendedOverlay";
import { WorkspaceTrustBar } from "@/components/WorkspaceTrustBar";
import { CreditGateModalHost } from "@/components/billing/CreditGateModalHost";
import { AffiliateReferralNotice } from "@/components/affiliate/AffiliateReferralNotice";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { appChromeCssVars } from "@/lib/appChrome";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { getBrandConfig } from "@/config/brand";
import { cn } from "@/lib/utils";
import { staffDepartmentService } from "@/services/staffDepartmentService";

interface LayoutProps {
    children: ReactNode;
}

const NavItemLink = ({ item, pathname }: { item: NavItem; pathname: string }) => {
    const Icon = item.icon;
    const isActive = item.active(pathname);
    const [isExpanded, setIsExpanded] = useState(() => item.children ? isActive : false);

    useEffect(() => {
        if (item.children && isActive) {
            setIsExpanded(true);
        }
    }, [isActive, item.children]);

    if (item.children) {
        return (
            <div>
                <button
                    type="button"
                    onClick={() => setIsExpanded((current) => !current)}
                    className={cn(
                        "group flex w-full items-center gap-3 rounded-md px-3 py-3 text-left text-sm transition-colors",
                        isActive
                            ? "bg-primary/10 text-primary"
                            : "text-foreground/80 hover:bg-muted hover:text-foreground"
                    )}
                    aria-expanded={isExpanded}
                >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold leading-tight">{item.label}</span>
                        <span className="block truncate text-xs text-muted-foreground">{item.description}</span>
                    </span>
                    <ChevronDown
                        className={cn(
                            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                            isExpanded && "rotate-180"
                        )}
                    />
                </button>
                {isExpanded ? (
                    <div className="mt-1 space-y-1 pl-11">
                        {item.children.map((child) => {
                            const childActive = child.active(pathname);
                            return (
                                <Link
                                    key={`${item.href}-${child.href}`}
                                    to={child.href}
                                    className={cn(
                                        "block rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                                        childActive
                                            ? "bg-primary/10 text-primary"
                                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                    )}
                                >
                                    {child.label}
                                </Link>
                            );
                        })}
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <Link
            to={item.href}
            className={cn(
                "group flex touch-target items-center gap-3 rounded-md px-3 py-3 text-sm transition-colors",
                isActive
                    ? "bg-primary/10 text-primary"
                    : "text-foreground/80 hover:bg-muted hover:text-foreground"
            )}
        >
            <Icon className="h-5 w-5 shrink-0" />
            <span className="min-w-0">
                <span className="block truncate font-semibold leading-tight">{item.label}</span>
                <span className="block truncate text-xs text-muted-foreground">{item.description}</span>
            </span>
        </Link>
    );
};

const DepartmentNavItem = ({ pathname }: { pathname: string }) => {
    const { t } = useTranslation();
    const isActive = pathname === "/ai-agents/insights" || pathname.startsWith("/ai-agents/departments");
    const [isExpanded, setIsExpanded] = useState(isActive);
    const departmentsQuery = useQuery({
        queryKey: ["staff-departments"],
        queryFn: staffDepartmentService.listDepartments,
        staleTime: 1000 * 60,
    });
    const departments = departmentsQuery.data?.departments ?? [];

    useEffect(() => {
        if (isActive) {
            setIsExpanded(true);
        }
    }, [isActive]);

    return (
        <div>
            <div
                className={cn(
                    "group flex items-center gap-2 rounded-md px-3 py-3 text-sm transition-colors",
                    isActive
                        ? "bg-primary/10 text-primary"
                        : "text-foreground/80 hover:bg-muted hover:text-foreground"
                )}
            >
                <Link to="/ai-agents/insights" className="flex min-w-0 flex-1 items-center gap-3">
                    <Home className="h-5 w-5 shrink-0" />
                    <span className="min-w-0">
                        <span className="block truncate font-semibold leading-tight">{t("nav.aiStaff")}</span>
                        <span className="block truncate text-xs text-muted-foreground">{t("nav.departments")}</span>
                    </span>
                </Link>
                <button
                    type="button"
                    onClick={() => setIsExpanded((current) => !current)}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background/70 hover:text-foreground"
                    aria-label={t("nav.toggleDepartments")}
                    aria-expanded={isExpanded}
                >
                    <ChevronDown
                        className={cn(
                            "h-4 w-4 transition-transform",
                            isExpanded && "rotate-180"
                        )}
                    />
                </button>
            </div>
            {isExpanded ? (
                <div className="mt-1 space-y-1 pl-11">
                    {departmentsQuery.isLoading ? (
                        <div className="px-3 py-1.5 text-xs text-muted-foreground">{t("nav.loadingDepartments")}</div>
                    ) : departments.length === 0 ? (
                        <div className="px-3 py-1.5 text-xs text-muted-foreground">{t("nav.noDepartments")}</div>
                    ) : (
                        departments.map((department) => {
                            const href = `/ai-agents/departments/${department.id}`;
                            const childActive = pathname === href;
                            return (
                                <Link
                                    key={department.id}
                                    to={href}
                                    className={cn(
                                        "flex min-w-0 items-center justify-between gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                                        childActive
                                            ? "bg-primary/10 text-primary"
                                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                    )}
                                >
                                    <span className="truncate">{department.name}</span>
                                    <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                                        {department.task_count}
                                    </span>
                                </Link>
                            );
                        })
                    )}
                </div>
            ) : null}
        </div>
    );
};

const AppShell = ({ children }: LayoutProps) => {
    const { t } = useTranslation();
    const navItems = useNavItems();
    const location = useLocation();
    const { user, logout } = useAuth();
    const { data: tenant } = useTenant();
    const brand = getBrandConfig();
    const canManageCompany = Boolean(user?.is_admin || user?.is_company_admin);
    const canManageDeveloper = Boolean(user?.is_admin || user?.is_developper);

    const handleLogout = () => {
        void logout();
    };

    const lockChatViewport =
      location.pathname.startsWith("/ai-agents") &&
      !location.pathname.startsWith("/ai-agents/insights") &&
      !location.pathname.startsWith("/ai-agents/departments");

    return (
        <div
            className={
                lockChatViewport
                    ? "flex h-[var(--app-viewport-height)] max-h-[var(--app-viewport-height)] flex-col overflow-hidden bg-muted/20 text-foreground [--app-mobile-nav-height:0px]"
                    : "min-h-screen bg-muted/20 text-foreground [--app-mobile-nav-height:0px] lg:[--app-mobile-nav-height:0px]"
            }
            style={appChromeCssVars as CSSProperties}
        >
            <aside className="fixed inset-y-0 left-0 z-40 hidden w-[236px] flex-col border-r border-border/70 bg-background lg:flex">
                <div className="flex h-[68px] items-center justify-center border-b border-border/70 px-6">
                    <Link to="/ai-agents/insights" className="flex min-w-0 justify-center">
                        <img
                            src={brand.paths.logo}
                            alt={brand.logoAlt}
                            className="h-9 w-auto object-contain"
                        />
                    </Link>
                </div>

                <nav className="flex-1 space-y-1 overflow-y-auto px-5 py-5">
                    <DepartmentNavItem pathname={location.pathname} />
                    {navItems.slice(1, 6).map((item) => (
                        <NavItemLink key={item.href + item.label} item={item} pathname={location.pathname} />
                    ))}

                    <div className="my-5 border-t border-border/70" />

                    {navItems.slice(6).map((item) => (
                        <NavItemLink key={item.href + item.label} item={item} pathname={location.pathname} />
                    ))}
                </nav>

            </aside>

            <div
                className={
                    lockChatViewport
                        ? "flex h-full min-h-0 flex-col overflow-hidden lg:pl-[236px]"
                        : "flex min-h-screen flex-col lg:pl-[236px]"
                }
            >
                <header className="sticky top-0 z-30 flex h-[68px] shrink-0 items-center justify-between border-b border-border/70 bg-background/95 px-4 backdrop-blur sm:px-6 lg:px-8">
                    <div className="flex min-w-0 items-center gap-2">
                        <Sheet>
                            <SheetTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="touch-target shrink-0 lg:hidden"
                                    aria-label={t("layout.openNavMenu")}
                                >
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="flex h-full min-h-0 w-[min(100vw-2rem,320px)] flex-col p-0">
                                <SheetHeader className="shrink-0 border-b px-5 py-4 text-left">
                                    <SheetTitle className="text-base">{t("layout.menu")}</SheetTitle>
                                </SheetHeader>
                                <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-4">
                                    <DepartmentNavItem pathname={location.pathname} />
                                    {navItems.slice(1, 6).map((item) => (
                                        <NavItemLink key={`drawer-${item.href}-${item.label}`} item={item} pathname={location.pathname} />
                                    ))}
                                    <div className="my-4 border-t border-border/70" />
                                    {navItems.slice(6).map((item) => (
                                        <NavItemLink key={`drawer-${item.href}-${item.label}`} item={item} pathname={location.pathname} />
                                    ))}
                                </nav>
                            </SheetContent>
                        </Sheet>
                        <div className="min-w-0">
                            <div className="text-lg font-semibold leading-tight">{t("layout.hubTitle")}</div>
                            <div className="text-xs text-muted-foreground">{t("layout.hubSubtitle")}</div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <LanguageSwitcher />
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-10 gap-2 px-2">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                                        {user?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || "U"}
                                    </div>
                                    <span className="hidden text-left sm:block">
                                        <span className="block text-sm font-semibold leading-tight">{user?.full_name || t("common.user")}</span>
                                        <span className="block text-xs text-muted-foreground">{user?.is_admin ? t("common.admin") : t("common.member")}</span>
                                    </span>
                                    <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="max-h-[min(70vh,var(--radix-dropdown-menu-content-available-height))] w-56 overflow-y-auto"
                            >
                                <DropdownMenuLabel>
                                    <div className="space-y-1">
                                        <p className="text-sm font-medium">{user?.full_name || t("common.user")}</p>
                                        <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                                        {tenant ? (
                                            <p className="truncate text-xs text-muted-foreground">{tenant.company_name}</p>
                                        ) : null}
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild>
                                    <Link to="/account">
                                        <UserCog className="mr-2 h-4 w-4" />
                                        {t("nav.accountSettings")}
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link to="/my/credits">
                                        <Coins className="mr-2 h-4 w-4" />
                                        {t("nav.myCredits")}
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link to="/account/promotion-codes">
                                        <Ticket className="mr-2 h-4 w-4" />
                                        {t("nav.promotionCodes")}
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link to="/affiliate">
                                        <Share2 className="mr-2 h-4 w-4" />
                                        {t("nav.affiliate")}
                                    </Link>
                                </DropdownMenuItem>
                                {canManageDeveloper ? (
                                    <DropdownMenuItem asChild>
                                        <Link to="/developer/api-keys">
                                            <KeyRound className="mr-2 h-4 w-4" />
                                            {t("nav.developerApi")}
                                        </Link>
                                    </DropdownMenuItem>
                                ) : null}
                                {canManageCompany ? (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem asChild>
                                            <Link to="/company/users">
                                                <Users className="mr-2 h-4 w-4" />
                                                {t("nav.companyUsers")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/company/settings">
                                                <Building2 className="mr-2 h-4 w-4" />
                                                {t("nav.companySettings")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/company/roles">
                                                <SlidersHorizontal className="mr-2 h-4 w-4" />
                                                {t("nav.rolesLimits")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/company/approvals">
                                                <FileCheck className="mr-2 h-4 w-4" />
                                                {t("nav.approvals")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/company/credits">
                                                <Coins className="mr-2 h-4 w-4" />
                                                {t("nav.credits")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/company/storage">
                                                <HardDrive className="mr-2 h-4 w-4" />
                                                {t("nav.storage")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/company/usage">
                                                <Bot className="mr-2 h-4 w-4" />
                                                {t("nav.companyUsage")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/company/billing">
                                                <CreditCard className="mr-2 h-4 w-4" />
                                                {t("nav.billing")}
                                            </Link>
                                        </DropdownMenuItem>
                                    </>
                                ) : null}
                                {user?.is_admin ? (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin/customers">
                                                <UserCircle className="mr-2 h-4 w-4" />
                                                {t("nav.customers")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin">
                                                <Settings className="mr-2 h-4 w-4" />
                                                {t("nav.websiteSettings")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin/companies">
                                                <Building2 className="mr-2 h-4 w-4" />
                                                {t("nav.companies")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin/users">
                                                <Users className="mr-2 h-4 w-4" />
                                                {t("nav.userManagement")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin/trial-settings">
                                                <SlidersHorizontal className="mr-2 h-4 w-4" />
                                                {t("nav.trialSettings")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin/credits">
                                                <Coins className="mr-2 h-4 w-4" />
                                                {t("nav.creditSettings")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin/affiliate">
                                                <Share2 className="mr-2 h-4 w-4" />
                                                {t("nav.affiliate")}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/admin/promotion-codes">
                                                <Ticket className="mr-2 h-4 w-4" />
                                                {t("nav.promotionCodesAdmin")}
                                            </Link>
                                        </DropdownMenuItem>
                                    </>
                                ) : null}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                                    <LogOut className="mr-2 h-4 w-4" />
                                    {t("common.signOut")}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                <main
                    className={
                        lockChatViewport
                            ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                            : "flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto"
                    }
                >
                    <Suspense fallback={<div className="px-4 py-3 sm:px-6 lg:px-8"><TopLoadingBar /></div>}>
                        {children}
                    </Suspense>
                </main>

                <WorkspaceTrustBar />
            </div>
        </div>
    );
};

export const Layout = ({ children }: LayoutProps) => {
    const location = useLocation();
    const { isSuspended, isPastDue, paymentGracePeriodEndsAt } = useCompanyStatus();
    const allowBillingRecovery = location.pathname === "/company/billing";

    return (
        <>
            {isPastDue ? (
                <PaymentDueBanner paymentGracePeriodEndsAt={paymentGracePeriodEndsAt} />
            ) : null}
            <AppShell>{children}</AppShell>
            {isSuspended && !allowBillingRecovery ? <SuspendedOverlay /> : null}
            <CreditGateModalHost />
            <AffiliateReferralNotice />
        </>
    );
};
